import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  Search, ShieldAlert, DollarSign, Eye, ExternalLink, ArrowRight, 
  TrendingUp, AlertCircle, ShoppingBag, Loader2, Zap, Clock
} from "lucide-react";
import {
  getBestSellers,
  getMarketBenchmark,
  getDetectedCompetitors
} from "@/lib/api";

const MarketThreatCard = ({ product, benchmark, loading }) => {
  if (loading) return <div className="h-64 bg-muted animate-pulse rounded-xl border" />;

  const hasGap = benchmark?.comparison?.is_more_expensive;
  const competitor = benchmark?.competitor;

  return (
    <Card className="border-border/50 hover:border-primary/20 transition-all shadow-sm overflow-hidden">
      <div className="grid md:grid-cols-2">
        {/* Your Product */}
        <div className="p-5 bg-primary/[0.01] border-r">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Your Product</p>
              <h3 className="font-bold text-sm truncate w-48">{product.title}</h3>
            </div>
          </div>
          
          <div className="flex items-baseline gap-2 mb-4">
            <span className="text-3xl font-bold text-foreground">${product.price}</span>
            <span className="text-xs text-muted-foreground">Retail Price</span>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs p-2 bg-background rounded-md border">
              <span className="text-muted-foreground">Sales Velocity</span>
              <span className="font-medium">{product.total_sold} sold</span>
            </div>
          </div>
        </div>

        {/* Market Threat */}
        <div className={`p-5 ${hasGap ? 'bg-orange-50/30' : 'bg-secondary/20'}`}>
          {competitor ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                    <ShieldAlert className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-orange-600">Rival Offer</p>
                    <h3 className="font-bold text-sm truncate w-40">{competitor.title}</h3>
                  </div>
                </div>
                {hasGap && <Badge variant="warning" className="text-[9px]">PRICE THREAT</Badge>}
              </div>

              <div className="flex items-baseline gap-2 mb-4">
                <span className={`text-3xl font-bold ${hasGap ? 'text-orange-600' : 'text-foreground'}`}>
                  ${competitor.price || '??'}
                </span>
                <span className="text-xs text-muted-foreground">Competitor Price</span>
              </div>

              <div className="space-y-2">
                {benchmark.comparison && (
                  <div className={`text-xs p-2 rounded-md font-medium border ${hasGap ? 'bg-orange-100/50 border-orange-200 text-orange-800' : 'bg-emerald-100/50 border-emerald-200 text-emerald-800'}`}>
                    {benchmark.comparison.recommendation}
                  </div>
                )}
                {!competitor.in_stock && (
                  <div className="text-xs p-2 bg-emerald-500 text-white rounded-md font-bold flex items-center gap-2">
                    <Zap className="w-3 h-3" /> SCALING OPPORTUNITY: Rival is Out of Stock!
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center py-4">
              <Search className="w-8 h-8 text-muted-foreground mb-2 opacity-20" />
              <p className="text-xs text-muted-foreground">Scanning market for matches...</p>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default function ProductIntelligencePage() {
  const [searchParams] = useSearchParams();
  const selectedProductId = searchParams.get('product');
  
  const [products, setProducts] = useState([]);
  const [benchmarks, setBenchmarks] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadThreatData = useCallback(async () => {
    setLoading(true);
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const shop = urlParams.get('shop');
      
      const response = await getBestSellers(shop);
      if (response.success) {
        const sellers = response.data.products;
        setProducts(sellers);

        // Fetch market benchmarks for the top product or selected one
        const target = selectedProductId 
          ? sellers.find(p => p.id === parseInt(selectedProductId)) 
          : sellers[0];

        if (target) {
          // In a real scenario, we'd have the competitor URL from previous discovery
          // For MVP, we'll benchmark against a common competitor domain
          const mockAdUrl = `https://www.competitor.com/products/${target.handle}`;
          const benchmarkData = await getMarketBenchmark(mockAdUrl, target.id);
          
          if (benchmarkData.success) {
            setBenchmarks({ [target.id]: benchmarkData.data });
          }
        }
      }
    } catch (err) {
      console.error('Threat data failed:', err);
      setError("Market monitoring temporarily unavailable.");
    } finally {
      setLoading(false);
    }
  }, [selectedProductId]);

  useEffect(() => {
    loadThreatData();
  }, [loadThreatData]);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8 animate-in fade-in duration-500">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground flex items-center gap-3">
          <ShieldAlert className="w-8 h-8 text-primary" /> Market Threat Analysis
        </h1>
        <p className="text-muted-foreground mt-2">
          Comparing your best-sellers against active market rivals to identify revenue risks and scaling opportunities.
        </p>
      </div>

      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5" /> {error}
        </div>
      )}

      {/* Main Comparison Section */}
      <div className="space-y-6">
        <h2 className="text-lg font-bold text-foreground">Critical Vulnerabilities</h2>
        {loading ? (
          <div className="h-64 bg-muted animate-pulse rounded-xl" />
        ) : products.length > 0 ? (
          <div className="space-y-6">
            {products.slice(0, 3).map(product => (
              <MarketThreatCard 
                key={product.id} 
                product={product} 
                benchmark={benchmarks[product.id]}
                loading={loading}
              />
            ))}
          </div>
        ) : (
          <Card className="border-dashed py-20">
            <div className="text-center">
              <ShoppingBag className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-20" />
              <p className="text-muted-foreground">No best-sellers found to analyze.</p>
              <Button variant="link" onClick={loadThreatData}>Refresh Store Data</Button>
            </div>
          </Card>
        )}
      </div>

      {/* Market Strategy Feed */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-500" /> Scaling Opportunities
            </CardTitle>
            <CardDescription>Exploit market gaps detected in the last 24h</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-lg">
              <p className="text-xs font-bold text-emerald-700 uppercase mb-1">Stock Gap</p>
              <p className="text-sm text-emerald-900">Your top competitor for "Hydrating Serum" just went out of stock. Increase ad spend by 20% to capture their traffic.</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" /> Longevity Watch
            </CardTitle>
            <CardDescription>Rival creative structures that are consistently winning</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg">
              <p className="text-xs font-bold text-gray-900 uppercase mb-1">Creative Trend</p>
              <p className="text-sm text-gray-900">3 rivals in your category have scaled "User Unboxing" videos for 45+ days. This hook is currently the market winner.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
